import { useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { cloneRecord, deleteRecord, fetchRecords } from '../services/ProductsService';
import { useState } from 'react';
import { ProductStatus } from '../types';
import { notifySuccess, notifyError } from '../../../components/ServiceNotifications';

export const useProducts = (externalStatusFilter?: ProductStatus | null) => {
    const queryClient = useQueryClient();
    const [keyword, setKeyword] = useState('');
    const [statusFilter, setStatusFilter] = useState<ProductStatus | null>(null);
    const [selection, setSelection] = useState<Record<string, boolean>>({});
    
    // Use external filter if provided, otherwise use internal filter
    const activeStatusFilter = externalStatusFilter !== undefined ? externalStatusFilter : statusFilter;
    
    const limit = 10;
    const sort = { column: 'created', direction: 'desc' as const };
    
    // INFINITE QUERY
    const productsQuery = useInfiniteQuery({
        queryKey: ['products', keyword, activeStatusFilter, limit, sort.column, sort.direction],
        queryFn: ({ pageParam }: { pageParam: string | null }) => fetchRecords({ limit, sort, nextToken: pageParam, statusFilter: activeStatusFilter }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null
    });

    const deleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const promises = ids.map(id => deleteRecord(id));
            return Promise.all(promises);
        },
        onSuccess: () => {
            notifySuccess('Product deleted');
            queryClient.invalidateQueries({ queryKey: ['products'] });
            queryClient.invalidateQueries({ queryKey: ['all-products'] });
        },
        onError: (error) => {
            notifyError(error);
        }
    });

    const cloneMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const promises = ids.map(id => cloneRecord(id));
            return Promise.all(promises);
        },
        onSuccess: () => {
            notifySuccess('Product cloned');
            queryClient.invalidateQueries({ queryKey: ['products'] });
            queryClient.invalidateQueries({ queryKey: ['all-products'] });
        },
        onError: (error) => {
            notifyError(error);
        }
    });

    const fetchRecordsFunc = () => {
        if (productsQuery.hasNextPage) {
            productsQuery.fetchNextPage();
        }
    };

    const deleteRecords = (ids?: string[]) => {
        const selectedIds = ids || Object.keys(selection);
        if (selectedIds.length > 0) {
            deleteMutation.mutate(selectedIds);
        }
    };

    const cloneRecords = () => {
        const selectedIds = Object.keys(selection);
        if (selectedIds.length > 0) {
            cloneMutation.mutate(selectedIds);
        }
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    const handleFilterByStatus = (status: ProductStatus | null) => {
        setStatusFilter(status);
    };

    const allItems = productsQuery.data?.pages?.flatMap(page => (page && 'items' in page) ? page.items : []) || [];

    return {
        items: allItems,
        visibleItems: allItems,
        loading: productsQuery.isLoading,
        keyword,
        visibleCount: allItems.length,
        totalCount: allItems.length,
        nextToken: productsQuery.hasNextPage ? 'has-more' : null,
        fetchRecords: fetchRecordsFunc,
        deleteRecords,
        cloneRecords,
        handleFilterByKeyword,
        handleFilterByStatus,
        statusFilter,
        deleteMutation,
        cloneMutation,
        selection,
        setSelection
    };
};